'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { PropsWithChildren } from 'react';

// EWM-613 — flush-before-checkout registry.
//
// Each cart line-item row owns a per-item `useOptimisticQuantity` whose `flush()`
// commits any trailing-debounced quantity immediately. The Proceed-to-Checkout button
// lives in CartOrderSummary — a SIBLING of the rows — so it can't reach those per-row
// flush fns directly. This tiny ref-backed registry lets each row REGISTER its flush on
// mount and lets the Proceed button FLUSH ALL pending qty writes before navigating to
// /checkout, so a mid-debounce quantity isn't lost ("checkout used my old qty"). The
// registry is a ref, so registering/unregistering causes no re-render and no global lock.
type FlushFn = () => Promise<void>;

interface CartFlushRegistry {
  register: (id: string, flush: FlushFn) => void;
  unregister: (id: string) => void;
  flushAll: () => Promise<void>;
}

const CartFlushContext = createContext<CartFlushRegistry | null>(null);

export const CartFlushRegistryProvider = ({ children }: PropsWithChildren) => {
  const registry = useRef(new Map<string, FlushFn>());

  const register = useCallback((id: string, flush: FlushFn) => {
    registry.current.set(id, flush);
  }, []);

  const unregister = useCallback((id: string) => {
    registry.current.delete(id);
  }, []);

  const flushAll = useCallback(async () => {
    // Snapshot the current flushes; a row that unmounts mid-flush still resolves its
    // own pending write. Per-flush failures are swallowed so one bad row can't block
    // navigation to checkout.
    await Promise.all([...registry.current.values()].map((flush) => flush().catch(() => {})));
  }, []);

  const value = useMemo(() => ({ register, unregister, flushAll }), [register, unregister, flushAll]);

  return <CartFlushContext.Provider value={value}>{children}</CartFlushContext.Provider>;
};

// Row hook — registers this row's `flush` for the lifetime of the row. Outside a
// provider (Storybook, isolated tests) it's a no-op.
export const useRegisterCartFlush = (id: string, flush: FlushFn) => {
  const ctx = useContext(CartFlushContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.register(id, flush);
    return () => ctx.unregister(id);
  }, [ctx, id, flush]);
};

// Consumer hook for the Proceed-to-Checkout boundary — returns a stable fn that flushes
// every registered row's pending qty write. No-op (resolves immediately) without a provider.
export const useFlushPendingCartWrites = (): (() => Promise<void>) => {
  const ctx = useContext(CartFlushContext);
  return useCallback(() => ctx?.flushAll() ?? Promise.resolve(), [ctx]);
};
